import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import { ChevronLeft, ChevronRight, Edit, X, Save, Loader2, UserPlus, Trash2, Calendar as CalendarIcon, Search, Download, ArrowUp, ArrowDown, FileSpreadsheet, ClipboardCheck } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Avatar, AvatarFallback } from "../../components/ui/avatar";
import { Switch } from "../../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { LeaveHandoverSummary } from "../../components/LeaveHandoverSummary";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { api } from "../../utils/api";
import { toast } from "sonner";
import AccessRestricted from "../../components/AccessRestricted";

type TabType = "Leave Balances" | "Policy Settings" | "Audit Trail" | "Delegation Authority" | "Accruals" | "Daily Hours" | "Balance Movements";

const ALL_TABS: TabType[] = ["Leave Balances", "Policy Settings", "Audit Trail", "Delegation Authority", "Accruals", "Daily Hours", "Balance Movements"];

const TAB_RESOURCES: Record<TabType, string> = {
  "Leave Balances": RESOURCES.LEAVE_BALANCES_TAB,
  "Policy Settings": RESOURCES.LEAVE_POLICY_SETTINGS_TAB,
  "Audit Trail": RESOURCES.LEAVE_AUDIT_TRAIL_TAB,
  "Delegation Authority": RESOURCES.LEAVE_DELEGATION_AUTHORITY_TAB,
  "Accruals": RESOURCES.LEAVE_ACCRUALS_TAB,
  "Daily Hours": RESOURCES.LEAVE_DAILY_HOURS_TAB,
  "Balance Movements": RESOURCES.LEAVE_BALANCE_MOVEMENTS_TAB,
};

interface EmployeeBalance {
  id: string;
  name: string;
  initials: string;
  annual: string;
  sick: number;
  personal: string;
}

interface EmployeeAccrual {
  id: string;
  name: string;
  initials: string;
  annualRate: string;
  personalRate: string;
  standardWeeklyHours: string;
  enabled: boolean;
  accrualStartDate?: string | null;
}

// Raw shape of GET /api/attendance/team-accruals — the derived weekly entitlement
// is sent as decimal strings so precision survives the round-trip.
interface TeamAccrualRow {
  id: string;
  name: string;
  initials: string;
  annualRate?: string | number | null;
  personalRate?: string | number | null;
  standardWeeklyHours?: string | number | null;
  enabled?: boolean;
  accrualStartDate?: string | null;
}

interface EmployeeDailyHours {
  id: string;
  name: string;
  initials: string;
  standardDailyHours: number;
}

interface AuditAdjustment {
  id: string;
  createdAt: string;
  employee: { name: string };
  leaveType: string;
  adjustmentHours: number | string;
  reason: string;
  adjustedBy: { name: string };
}

interface Delegation {
  id: string;
  originalManager: { id: string, name: string };
  nominatedManager: { id: string, name: string };
  startDate: string;
  endDate: string;
  isActive: boolean;
  createdAt: string;
}

interface EligibleDelegate {
  id: string;
  name: string;
  userRole: string;
}

const LeaveAdmin = () => {
  const navigate = useNavigate();
  const ability = useAbility(AbilityContext);
  const canManageTab = (tab: TabType) => ability.can(ACTIONS.MANAGE, TAB_RESOURCES[tab]);
  const visibleTabs = ALL_TABS.filter(canManageTab);
  const [activeTab, setActiveTab] = useState<TabType>("Leave Balances");
  // Every visible tab is fully manageable by its holder, so edit controls
  // key off the active tab's manage permission.
  const canEdit = canManageTab(activeTab);
  const [employeeBalances, setEmployeeBalances] = useState<EmployeeBalance[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageAudit, setCurrentPageAudit] = useState(1);
  const ITEMS_PER_PAGE = 15;
  const [adjustments, setAdjustments] = useState<AuditAdjustment[]>([]);
  const [auditSearchTerm, setAuditSearchTerm] = useState("");
  const [delegations, setDelegations] = useState<Delegation[]>([]);
  const [eligibleDelegates, setEligibleDelegates] = useState<EligibleDelegate[]>([]);
  const [loading, setLoading] = useState(true);
  const [delegationModalOpen, setDelegationModalOpen] = useState(false);

  // Balance Movements State
  const [movements, setMovements] = useState<any[]>([]);
  const [movementSearchTerm, setMovementSearchTerm] = useState("");
  const [movementLeaveTypeFilter, setMovementLeaveTypeFilter] = useState<string>("All");
  const [movementTypeFilter, setMovementTypeFilter] = useState<string>("All");
  const [movementStartDate, setMovementStartDate] = useState("");
  const [movementEndDate, setMovementEndDate] = useState("");
  const [currentPageMovements, setCurrentPageMovements] = useState(1);
  const [movementSortDirection, setMovementSortDirection] = useState<'asc' | 'desc'>('desc');
  const [handoverMovement, setHandoverMovement] = useState<any | null>(null);

  // Accruals State
  const [employeeAccruals, setEmployeeAccruals] = useState<EmployeeAccrual[]>([]);
  const [accrualSearchTerm, setAccrualSearchTerm] = useState("");
  const [savingAccrualId, setSavingAccrualId] = useState<string | null>(null);
  const [currentPageAccruals, setCurrentPageAccruals] = useState(1);

  // Daily Hours State
  const [employeeDailyHours, setEmployeeDailyHours] = useState<EmployeeDailyHours[]>([]);
  const [dailyHoursSearchTerm, setDailyHoursSearchTerm] = useState("");
  const [savingDailyHoursId, setSavingDailyHoursId] = useState<string | null>(null);
  const [currentPageDailyHours, setCurrentPageDailyHours] = useState(1);

  // Policy Settings State
  const [policyData, setPolicyData] = useState({
    defaultAnnualLeave: 160,
    defaultSickLeave: 80,
    defaultPersonalLeave: 40,
    allowCarryOver: true,
    maxCarryOverHours: 5,
    minAdvanceBookingDays: 7,
    enableHalfDay: true,
    leaveAdmins: [] as string[]
  });
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [adminSearchTerm, setAdminSearchTerm] = useState("");

  const [savingPolicy, setSavingPolicy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [delegationData, setDelegationData] = useState({
    nominatedManagerId: "",
    startDate: "",
    endDate: ""
  });

  useEffect(() => {
    if (!canManageTab(activeTab)) return;

    if (activeTab === "Leave Balances") {
      fetchBalances();
    } else if (activeTab === "Audit Trail") {
      fetchAdjustments();
    } else if (activeTab === "Policy Settings") {
      fetchSettings();
      fetchAllUsers();
    } else if (activeTab === "Delegation Authority") {
      fetchDelegations();
      fetchEligibleDelegates();
    } else if (activeTab === "Accruals") {
      fetchAccruals();
    } else if (activeTab === "Daily Hours") {
      fetchDailyHours();
    } else if (activeTab === "Balance Movements") {
      fetchMovements();
    }
  }, [activeTab]);

  // Fall back to the first permitted tab if the active tab is not permitted
  useEffect(() => {
    const permitted = ALL_TABS.filter(tab => ability.can(ACTIONS.MANAGE, TAB_RESOURCES[tab]));
    if (permitted.length > 0 && !permitted.includes(activeTab)) {
      setActiveTab(permitted[0]);
    }
  }, [activeTab, ability]);

  useEffect(() => {
    setCurrentPageAudit(1);
  }, [auditSearchTerm]);

  useEffect(() => {
    setCurrentPageAccruals(1);
  }, [accrualSearchTerm]);

  useEffect(() => {
    setCurrentPageMovements(1);
  }, [movementSearchTerm, movementLeaveTypeFilter, movementTypeFilter, movementStartDate, movementEndDate, movementSortDirection]);

  useEffect(() => {
    setCurrentPageDailyHours(1);
  }, [dailyHoursSearchTerm]);

  const fetchAccruals = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/attendance/team-accruals');
      if (res.success) {
        const rows = res.data as TeamAccrualRow[];
        const mapped = rows.map((u) => ({
          ...u,
          // The weekly entitlement is derived from the person's standard daily hours
          // (Daily Hours tab) — shown for reference, never edited here.
          annualRate: String(u.annualRate ?? '0'),
          personalRate: String(u.personalRate ?? '0'),
          standardWeeklyHours: String(u.standardWeeklyHours ?? '0'),
          enabled: u.enabled === true,
          accrualStartDate: u.accrualStartDate || null
        }));
        setEmployeeAccruals(mapped);
      }
    } catch (error) {
      console.error("Error fetching accruals:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAccrual = async (accrual: EmployeeAccrual) => {
    setSavingAccrualId(accrual.id);
    try {
      const res = await api.put(`/api/attendance/accruals/${accrual.id}`, {
        enabled: accrual.enabled,
        accrualStartDate: accrual.accrualStartDate || null
      });
      if (res.success) {
        toast.success("Accrual settings updated successfully");
        fetchAccruals();
      }
    } catch (error) {
      console.error("Error saving accruals:", error);
      toast.error("Failed to update accrual settings");
    } finally {
      setSavingAccrualId(null);
    }
  };

  const updateAccrualLocal = (id: string, field: keyof EmployeeAccrual, value: any) => {
    setEmployeeAccruals(prev => prev.map(a => a.id === id ? { ...a, [field]: value } : a));
  };

  const fetchDailyHours = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/attendance/team-daily-hours');
      if (res.success) {
        setEmployeeDailyHours(res.data);
      }
    } catch (error) {
      console.error("Error fetching daily hours:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDailyHours = async (emp: EmployeeDailyHours) => {
    setSavingDailyHoursId(emp.id);
    try {
      const res = await api.put(`/api/attendance/daily-hours/${emp.id}`, {
        standardDailyHours: emp.standardDailyHours
      });
      if (res.success) {
        toast.success("Daily hours updated successfully");
      }
    } catch (error) {
      console.error("Error saving daily hours:", error);
      toast.error("Failed to update daily hours");
    } finally {
      setSavingDailyHoursId(null);
    }
  };

  const updateDailyHoursLocal = (id: string, value: number) => {
    setEmployeeDailyHours(prev => prev.map(e => e.id === id ? { ...e, standardDailyHours: value } : e));
  };

  const fetchBalances = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/attendance/team-leave-balances');
      if (res.success) {
        setEmployeeBalances(res.data);
      }
    } catch (error) {
      console.error("Error fetching team balances:", error);
    } finally {
      setLoading(false);
    }
  };


  const fetchAllUsers = async () => {
    try {
      const res = await api.get('/api/users/approvers');
      if (res.success && res.users) {
        setAllUsers(res.users);
      }
    } catch (error) {
      console.error("Error fetching all users:", error);
    }
  };

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/attendance/settings');
      if (res.success) {
        setPolicyData({
          defaultAnnualLeave: parseFloat(res.data.defaultAnnualLeave),
          defaultSickLeave: parseFloat(res.data.defaultSickLeave),
          defaultPersonalLeave: parseFloat(res.data.defaultPersonalLeave),
          allowCarryOver: res.data.allowCarryOver,
          maxCarryOverHours: parseFloat(res.data.maxCarryOverHours),
          minAdvanceBookingDays: res.data.minAdvanceBookingDays,
          enableHalfDay: res.data.enableHalfDay,
          leaveAdmins: Array.isArray(res.data.leaveAdmins) ? res.data.leaveAdmins : []
        });
      }
    } catch (error) {
      console.error("Error fetching attendance settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdjustments = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/attendance/leave-adjustments');
      if (res.success) {
        setAdjustments(res.data);
      }
    } catch (error) {
      console.error("Error fetching adjustments:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMovements = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (movementLeaveTypeFilter && movementLeaveTypeFilter !== "All") {
        params.append("leaveType", movementLeaveTypeFilter);
      }

      const res = await api.get(`/api/attendance/leave-movements?${params.toString()}`);
      if (res.success) {
        setMovements(res.data);
      }
    } catch (error) {
      console.error("Error fetching leave movements:", error);
      toast.error("Failed to load balance movements");
    } finally {
      setLoading(false);
    }
  };

  const handleExportMovements = () => {
    const filtered = movements.filter(m => {
      const matchesSearch = !movementSearchTerm ||
        (m.employee?.name || "").toLowerCase().includes(movementSearchTerm.toLowerCase());
      const matchesLeaveType = movementLeaveTypeFilter === "All" || m.leaveType === movementLeaveTypeFilter;
      const matchesMovementType = movementTypeFilter === "All" || m.movementType === movementTypeFilter;
      const moveDate = new Date(m.effectiveDate || m.date);
      const matchesStart = !movementStartDate || moveDate >= new Date(movementStartDate);
      const matchesEnd = !movementEndDate || moveDate <= new Date(movementEndDate + 'T23:59:59');
      return matchesSearch && matchesLeaveType && matchesMovementType && matchesStart && matchesEnd;
    }).sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return movementSortDirection === 'asc' ? dateA - dateB : dateB - dateA;
    });

    if (filtered.length === 0) return;

    const headers = ["Date Approved", "Effective Date", "Employee", "Movement Type", "Leave Type", "Hours", "Description", "Authorised By", "Start Date", "End Date"];
    const csvContent = [
      headers.join(","),
      ...filtered.map(m => [
        new Date(m.approvedDate || m.date).toLocaleDateString(),
        m.metadata?.startDate && m.metadata?.endDate && m.metadata.startDate !== m.metadata.endDate
          ? `"${new Date(m.metadata.startDate).toLocaleDateString()} - ${new Date(m.metadata.endDate).toLocaleDateString()}"`
          : `"${m.effectiveDate ? new Date(m.effectiveDate).toLocaleDateString() : ''}"`,
        `"${m.employee?.name || 'N/A'}"`,
        `"${m.movementType}"`,
        `"${m.leaveType}"`,
        m.hours,
        `"${(m.description || '').replace(/"/g, '""')}"`,
        `"${m.performedBy?.name || 'N/A'}"`,
        `"${m.metadata?.startDate || ''}"`,
        `"${m.metadata?.endDate || ''}"`
      ].join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `leave_movements_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSaveSettings = async () => {
    setSavingPolicy(true);
    try {
      const res = await api.post('/api/attendance/settings', policyData);
      if (res.success) {
        toast.success("Policy settings updated successfully");
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Failed to update settings");
    } finally {
      setSavingPolicy(false);
    }
  };

  const fetchDelegations = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/attendance/delegations');
      if (res.success) {
        setDelegations(res.data);
      }
    } catch (error) {
      console.error("Error fetching delegations:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEligibleDelegates = async () => {
    try {
      const res = await api.get('/api/attendance/eligible-delegates');
      if (res.success) {
        setEligibleDelegates(res.data);
      }
    } catch (error) {
      console.error("Error fetching eligible delegates:", error);
    }
  };

  const handleSaveDelegation = async () => {
    if (!delegationData.nominatedManagerId || !delegationData.startDate || !delegationData.endDate) {
      toast.error("Please fill in all fields");
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post('/api/attendance/delegations', delegationData);
      if (res.success) {
        toast.success("Delegation authority assigned successfully");
        setDelegationModalOpen(false);
        fetchDelegations();
        setDelegationData({ nominatedManagerId: "", startDate: "", endDate: "" });
      }
    } catch (error) {
      console.error("Error saving delegation:", error);
      toast.error("Failed to assign delegation");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteDelegation = async (id: string) => {
    try {
      const res = await api.delete(`/api/attendance/delegations/${id}`);
      if (res.success) {
        toast.success("Delegation authority removed");
        fetchDelegations();
      }
    } catch (error) {
      console.error("Error deleting delegation:", error);
      toast.error("Failed to remove delegation");
    }
  };

  const openAdjustPage = (empId: string) => {
    navigate(`/attendance/adjust-balance/${empId}`);
  };

  if (visibleTabs.length === 0) {
    return (
      <AccessRestricted
        accessMessage="You don't have permission to manage leave administration."
        backMessage="Back to Attendance"
        navigateTo="/attendance"
      />
    );
  }

  return (
    <div className="max-w-[1200px] mx-auto pb-20 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <Button
          variant="outline"
          size="icon"
          className="rounded-xl border-gray-100 bg-white hover:bg-gray-50 shrink-0"
          onClick={() => navigate("/attendance")}
        >
          <ChevronLeft size={20} />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
            HR Administration
            {!canEdit && (
              <span className="text-sm font-normal text-gray-400 ml-2">(Read only)</span>
            )}
          </h1>
          <p className="text-gray-500 font-medium text-sm">Manage employee leave balances and policy settings</p>
        </div>
        {canManageTab("Leave Balances") && (
          <Button
            variant="outline"
            onClick={() => navigate("/attendance/leave-digest")}
            className="rounded-xl border-gray-200 bg-white hover:bg-gray-50 h-10 px-4 font-semibold text-gray-900 flex items-center gap-2 shrink-0 whitespace-nowrap"
          >
            <FileSpreadsheet className="h-4 w-4" />
            Leave Digest Report
          </Button>
        )}
      </div>

      <div className="space-y-6">
        {/* Tabs */}
        <div className="inline-flex bg-gray-100/80 p-1 rounded-xl">
          {visibleTabs.map((tab) => (
            <button
              key={tab}
              className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${activeTab === tab ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
                }`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === "Leave Balances" && canManageTab("Leave Balances") && (
          <div className="bg-white rounded-[24px] border border-gray-100 shadow-sm overflow-hidden">
            <div className="p-6 border-b border-gray-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <h3 className="font-bold text-gray-900 text-lg">Employee Leave Balances</h3>
                <p className="text-gray-500 text-sm font-medium">View and manage leave balances for all employees</p>
              </div>
              <div className="flex items-center gap-4 w-full md:w-auto">
                <div className="relative flex-1 md:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search employee..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="pl-9 rounded-xl border-gray-200 bg-gray-50/50 focus:bg-white h-10 text-sm"
                  />
                </div>
                {loading && <Loader2 className="h-5 w-5 animate-spin text-gray-400" />}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="text-xs text-gray-900 font-bold bg-white border-b border-gray-100">
                  <tr>
                    <th className="px-6 py-4">Employee</th>
                    <th className="px-6 py-4 text-center">Annual Leave</th>
                    <th className="px-6 py-4 text-center">Personal Leave</th>
                    {canEdit && <th className="px-6 py-4 text-right">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {loading ? (
                    <tr>
                      <td colSpan={canEdit ? 4 : 3} className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center gap-2">
                          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                          <p className="text-gray-500 font-medium">Loading balances...</p>
                        </div>
                      </td>
                    </tr>
                  ) : employeeBalances.filter(emp => emp.name.toLowerCase().includes(searchTerm.toLowerCase())).length === 0 ? (
                    <tr>
                      <td colSpan={canEdit ? 4 : 3} className="px-6 py-12 text-center text-gray-500 italic">
                        No team members found matching "{searchTerm}".
                      </td>
                    </tr>
                  ) : (
                    (() => {
                      const filtered = employeeBalances.filter(emp => emp.name.toLowerCase().includes(searchTerm.toLowerCase()));
                      const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
                      const paginated = filtered.slice(startIndex, startIndex + ITEMS_PER_PAGE);

                      return paginated.map((row) => (
                        <tr key={row.id} className="hover:bg-gray-50/50 transition-colors">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <Avatar className="h-8 w-8 text-xs font-medium border border-gray-100 shadow-sm">
                                <AvatarFallback className="bg-gray-50 text-gray-600">
                                  {row.initials}
                                </AvatarFallback>
                              </Avatar>
                              <span className="text-gray-900 font-medium">{row.name}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <span className="px-3 py-1 bg-green-50 text-green-700 rounded-full text-xs font-bold border border-green-100">
                              {row.annual} hours
                            </span>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-bold border border-blue-100">
                              {row.personal} hours
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            {canEdit && (
                              <Button
                                variant="outline"
                                title="Adjust leave balance"
                                className="h-8 px-3 text-gray-700 font-bold hover:text-gray-900 hover:bg-gray-100 border-gray-200 rounded-lg flex items-center gap-2 ml-auto"
                                onClick={() => openAdjustPage(row.id)}
                              >
                                <Edit className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ));
                    })()
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {(() => {
              const filtered = employeeBalances.filter(emp => emp.name.toLowerCase().includes(searchTerm.toLowerCase()));
              const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
              if (totalPages <= 1) return null;

              return (
                <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between bg-gray-50/30">
                  <p className="text-xs text-gray-500 font-medium whitespace-nowrap">
                    Showing <span className="font-bold text-gray-900">{Math.min(filtered.length, (currentPage - 1) * ITEMS_PER_PAGE + 1)}</span> to <span className="font-bold text-gray-900">{Math.min(filtered.length, currentPage * ITEMS_PER_PAGE)}</span> of <span className="font-bold text-gray-900">{filtered.length}</span> employees
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      className="rounded-lg h-8 w-8 p-0 border-gray-200"
                    >
                      <ChevronLeft size={16} />
                    </Button>

                    <div className="flex items-center gap-1 mx-2">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                        // Show first page, last page, and pages around current page
                        if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                          return (
                            <button
                              key={page}
                              onClick={() => setCurrentPage(page)}
                              className={`h-8 w-8 rounded-lg text-xs font-bold transition-all ${currentPage === page
                                ? "bg-black text-white"
                                : "text-gray-500 hover:bg-gray-100"
                                }`}
                            >
                              {page}
                            </button>
                          );
                        }
                        // Ellipsis handle
                        if (page === 2 || page === totalPages - 1) {
                          return <span key={page} className="text-gray-400 text-xs px-1">...</span>;
                        }
                        return null;
                      })}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      className="rounded-lg h-8 w-8 p-0 border-gray-200"
                    >
                      <ChevronRight size={16} />
                    </Button>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {activeTab === "Policy Settings" && canManageTab("Policy Settings") && (
          <div className="space-y-6">
            <div className="bg-white rounded-[24px] border border-gray-100 shadow-sm overflow-hidden p-6 space-y-6">
              <div>
                <h3 className="font-bold text-gray-900 text-lg">Default Leave Entitlements</h3>
                <p className="text-gray-500 text-sm font-medium mb-4">Set default annual leave entitlements for new employees</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="space-y-2">
                        <label className="text-sm font-bold text-gray-900">Annual Leave (hours)</label>
                        <Input
                          type="number"
                          value={policyData.defaultAnnualLeave}
                          onChange={(e) => canEdit && setPolicyData({ ...policyData, defaultAnnualLeave: parseFloat(e.target.value) })}
                          className="rounded-xl border-gray-200 bg-gray-50/50 h-11 focus:bg-white text-gray-900"
                          disabled={!canEdit}
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-bold text-gray-900">Personal Leave (hours)</label>
                        <Input
                          type="number"
                          value={policyData.defaultPersonalLeave}
                          onChange={(e) => canEdit && setPolicyData({ ...policyData, defaultPersonalLeave: parseFloat(e.target.value) })}
                          className="rounded-xl border-gray-200 bg-gray-50/50 h-11 focus:bg-white text-gray-900"
                          disabled={!canEdit}
                        />
                      </div>
                    </div>
              </div>
            </div>

            <div className="bg-white rounded-[24px] border border-gray-100 shadow-sm overflow-hidden p-6 space-y-6">
              <div>
                <h3 className="font-bold text-gray-900 text-lg">Carry Over Rules</h3>
                <p className="text-gray-500 text-sm font-medium mb-4">Configure annual leave carry over policy</p>

                <div className="space-y-6">
                  {/* Toggle */}
                  <div className="flex items-center justify-between border border-gray-100 rounded-xl p-4 bg-gray-50/30">
                    <div>
                      <h4 className="font-semibold text-gray-900">Allow Carry Over</h4>
                      <p className="text-sm text-gray-500">Allow employees to carry over unused annual leave to next year</p>
                    </div>
                    <button
                      className={`w-11 h-6 rounded-full relative cursor-pointer px-1 flex items-center shrink-0 outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black transition-all ${policyData.allowCarryOver ? "bg-black" : "bg-gray-300"
                        }`}
                      onClick={() => canEdit && setPolicyData({ ...policyData, allowCarryOver: !policyData.allowCarryOver })}
                      disabled={!canEdit}
                    >
                      <div className={`absolute inline-block w-4 h-4 bg-white rounded-full transition-all ${policyData.allowCarryOver ? "right-1" : "left-1"
                        }`}></div>
                    </button>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-bold text-gray-900">Maximum Carry Over Hours</label>
                    <Input
                      type="number"
                      value={policyData.maxCarryOverHours}
                      onChange={(e) => setPolicyData({ ...policyData, maxCarryOverHours: parseFloat(e.target.value) })}
                      className="rounded-xl border-gray-200 bg-gray-50/50 h-11 focus:bg-white text-gray-900"
                      disabled={!canEdit}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-[24px] border border-gray-100 shadow-sm overflow-hidden p-6 space-y-6">
              <div>
                <h3 className="font-bold text-gray-900 text-lg">Request Settings</h3>
                <p className="text-gray-500 text-sm font-medium mb-4">Configure leave request requirements and rules</p>

                <div className="space-y-6">
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-gray-900">Minimum Advance Booking (days)</label>
                    <Input
                      type="number"
                      value={policyData.minAdvanceBookingDays}
                      onChange={(e) => setPolicyData({ ...policyData, minAdvanceBookingDays: parseInt(e.target.value) })}
                      className="rounded-xl border-gray-200 bg-gray-50/50 h-11 focus:bg-white text-gray-900"
                      disabled={!canEdit}
                    />
                    <p className="text-xs text-gray-500 mt-1">Minimum number of days in advance employees must request leave</p>
                  </div>

                  {/* Toggle */}
                  <div className="flex items-center justify-between border border-gray-100 rounded-xl p-4 bg-gray-50/30">
                    <div>
                      <h4 className="font-semibold text-gray-900">Enable Short Leave / Partial Days</h4>
                      <p className="text-sm text-gray-500">Allow employees to request leave for specific hours (e.g. 4 hours)</p>
                    </div>
                    <button
                      className={`w-11 h-6 rounded-full relative cursor-pointer px-1 flex items-center shrink-0 outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black transition-all ${policyData.enableHalfDay ? "bg-black" : "bg-gray-300"
                        }`}
                      onClick={() => canEdit && setPolicyData({ ...policyData, enableHalfDay: !policyData.enableHalfDay })}
                      disabled={!canEdit}
                    >
                      <div className={`absolute inline-block w-4 h-4 bg-white rounded-full transition-all ${policyData.enableHalfDay ? "right-1" : "left-1"
                        }`}></div>
                    </button>
                  </div>
                </div>
              </div>
            </div>


            <div className="bg-white rounded-[24px] border border-gray-100 shadow-sm overflow-hidden p-6 space-y-6">
              <div>
                <h3 className="font-bold text-gray-900 text-lg">Additional Leave Admins</h3>
                <p className="text-gray-500 text-sm font-medium mb-4">Grant specific users administrative access to the Attendance module</p>

                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search users..."
                    value={adminSearchTerm}
                    onChange={(e) => setAdminSearchTerm(e.target.value)}
                    className="pl-9 rounded-xl border-gray-200 bg-gray-50/50 focus:bg-white h-10 text-sm"
                  />
                </div>

                <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
                  {allUsers.filter(u => {
                    const roleCheck = u.userRole !== 'Admin' && u.userRole !== 'Director';
                    const term = adminSearchTerm.toLowerCase().trim();
                    const nameMatch = (u.name || "").toLowerCase().includes(term);
                    const roleMatch = (u.jobRole || "").toLowerCase().includes(term);
                    return roleCheck && (nameMatch || roleMatch);
                  }).map(user => {
                    const isSelected = policyData.leaveAdmins.includes(String(user.id));
                    return (
                      <div key={user.id} className="flex items-center justify-between border border-gray-100 rounded-xl p-3 bg-gray-50/30">
                        <div>
                          <h4 className="font-semibold text-gray-900">{user.name}</h4>
                          <p className="text-xs text-gray-500">{user.jobRole || 'Employee'}</p>
                        </div>
                        <button
                          className={`w-11 h-6 rounded-full relative cursor-pointer px-1 flex items-center shrink-0 outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black transition-all ${isSelected ? "bg-black" : "bg-gray-300"
                            }`}
                          onClick={() => {
                            if (!canEdit) return;
                            const userIdStr = String(user.id);
                            const newAdmins = isSelected
                              ? policyData.leaveAdmins.filter((id: string) => id !== userIdStr)
                              : [...policyData.leaveAdmins, userIdStr];
                            setPolicyData({ ...policyData, leaveAdmins: newAdmins });
                          }}
                          disabled={!canEdit}
                        >
                          <div className={`absolute inline-block w-4 h-4 bg-white rounded-full transition-all ${isSelected ? "right-1" : "left-1"
                            }`}></div>
                        </button>
                      </div>
                    )
                  })}
                  {allUsers.length === 0 && <p className="text-sm text-gray-500">Loading users...</p>}
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-2">
              {canEdit && (
                <Button
                  className="rounded-xl bg-black text-white hover:bg-gray-800 h-11 px-6 font-semibold flex items-center gap-2"
                  onClick={handleSaveSettings}
                  disabled={savingPolicy}
                >
                  {savingPolicy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Save Settings
                </Button>
              )}
            </div>
          </div>
        )}

        {activeTab === "Audit Trail" && canManageTab("Audit Trail") && (
          <div className="bg-white rounded-[24px] border border-gray-100 shadow-sm overflow-hidden">
            <div className="p-6 border-b border-gray-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h3 className="font-bold text-gray-900 text-lg">Adjustment Audit Trail</h3>
                <p className="text-gray-500 text-sm font-medium">History of all manual leave balance adjustments</p>
              </div>
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Filter by employee..."
                  value={auditSearchTerm}
                  onChange={(e) => setAuditSearchTerm(e.target.value)}
                  className="pl-9 rounded-xl border-gray-200 bg-gray-50/50 focus:bg-white h-10 text-sm"
                />
              </div>
              {loading && <Loader2 className="h-5 w-5 animate-spin text-gray-400" />}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="text-xs text-gray-900 font-bold bg-white border-b border-gray-100">
                  <tr>
                    <th className="px-6 py-4">Date</th>
                    <th className="px-6 py-4">Employee</th>
                    <th className="px-6 py-4">Leave Type</th>
                    <th className="px-6 py-4">Adjustment</th>
                    <th className="px-6 py-4">Reason</th>
                    <th className="px-6 py-4">Adjusted By</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-12 text-center text-gray-500 italic">
                        <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                        Loading history...
                      </td>
                    </tr>
                  ) : adjustments.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-12 text-center text-gray-500 italic">
                        No adjustments recorded yet.
                      </td>
                    </tr>
                  ) : (
                    (() => {
                      const filtered = adjustments.filter(adj =>
                        (adj.employee?.name ?? "").toLowerCase().includes(auditSearchTerm.toLowerCase()) ||
                        (adj.adjustedBy?.name ?? "").toLowerCase().includes(auditSearchTerm.toLowerCase()) ||
                        adj.reason.toLowerCase().includes(auditSearchTerm.toLowerCase())
                      );

                      const totalPagesAudit = Math.ceil(filtered.length / ITEMS_PER_PAGE);

                      if (filtered.length === 0) {
                        return (
                          <tr>
                            <td colSpan={6} className="px-6 py-12 text-center text-gray-500 italic">
                              No adjustments found matching "{auditSearchTerm}".
                            </td>
                          </tr>
                        );
                      }

                      const paginated = filtered.slice((currentPageAudit - 1) * ITEMS_PER_PAGE, currentPageAudit * ITEMS_PER_PAGE);

                      return paginated.map((row) => (
                        <tr key={row.id} className="hover:bg-gray-50/50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                            {new Date(row.createdAt).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 font-medium text-gray-900">{row.employee?.name ?? "Unknown"}</td>
                          <td className={`px-6 py-4 font-medium ${row.leaveType === 'Annual Leave' ? 'text-green-600' :
                              'text-blue-600'
                            }`}>
                            {row.leaveType}
                          </td>
                          <td className="px-6 py-4">
                            <span className={`px-2.5 py-1 rounded-md text-xs font-bold border ${Number(row.adjustmentHours) >= 0
                              ? "bg-green-50 text-green-700 border-green-100/50"
                              : "bg-red-50 text-red-700 border-red-100/50"
                              }`}>
                              {(() => {
                                const val = row.adjustmentHours;
                                if (val === undefined || val === null || val === '' || Number.isNaN(Number(val))) {
                                  return <span className="text-gray-400">—</span>;
                                }
                                // Full-precision value (exact decimal string from the API)
                                const sign = !String(val).startsWith('-') ? '+' : '';
                                return `${sign}${val} hours`;
                              })()}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-gray-600">{row.reason}</td>
                          <td className="px-6 py-4 text-gray-500">{row.adjustedBy?.name ?? "System"}</td>
                        </tr>
                      ));
                    })()
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls for Audit Trail */}
            {(() => {
              const filtered = adjustments.filter(adj =>
                (adj.employee?.name ?? "").toLowerCase().includes(auditSearchTerm.toLowerCase()) ||
                (adj.adjustedBy?.name ?? "").toLowerCase().includes(auditSearchTerm.toLowerCase()) ||
                adj.reason.toLowerCase().includes(auditSearchTerm.toLowerCase())
              );
              const totalPagesAudit = Math.ceil(filtered.length / ITEMS_PER_PAGE);
              if (totalPagesAudit <= 1) return null;

              return (
                <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between bg-gray-50/30">
                  <p className="text-xs text-gray-500 font-medium whitespace-nowrap">
                    Showing <span className="font-bold text-gray-900">{Math.min(filtered.length, (currentPageAudit - 1) * ITEMS_PER_PAGE + 1)}</span> to <span className="font-bold text-gray-900">{Math.min(filtered.length, currentPageAudit * ITEMS_PER_PAGE)}</span> of <span className="font-bold text-gray-900">{filtered.length}</span> audit logs
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={currentPageAudit === 1}
                      onClick={() => setCurrentPageAudit(prev => Math.max(1, prev - 1))}
                      className="rounded-lg h-8 w-8 p-0 border-gray-200"
                    >
                      <ChevronLeft size={16} />
                    </Button>

                    <div className="flex items-center gap-1 mx-2">
                      {Array.from({ length: totalPagesAudit }, (_, i) => i + 1).map(page => {
                        if (page === 1 || page === totalPagesAudit || (page >= currentPageAudit - 1 && page <= currentPageAudit + 1)) {
                          return (
                            <button
                              key={page}
                              onClick={() => setCurrentPageAudit(page)}
                              className={`h-8 w-8 rounded-lg text-xs font-bold transition-all ${currentPageAudit === page
                                ? "bg-black text-white"
                                : "text-gray-500 hover:bg-gray-100"
                                }`}
                            >
                              {page}
                            </button>
                          );
                        }
                        if (page === 2 || page === totalPagesAudit - 1) {
                          return <span key={page} className="text-gray-400 text-xs px-1">...</span>;
                        }
                        return null;
                      })}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      disabled={currentPageAudit === totalPagesAudit}
                      onClick={() => setCurrentPageAudit(prev => Math.min(totalPagesAudit, prev + 1))}
                      className="rounded-lg h-8 w-8 p-0 border-gray-200"
                    >
                      <ChevronRight size={16} />
                    </Button>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {activeTab === "Delegation Authority" && canManageTab("Delegation Authority") && (
          <div className="bg-white rounded-[24px] border border-gray-100 shadow-sm overflow-hidden">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
              <div>
                <h3 className="font-bold text-gray-900 text-lg">Leave Approval Delegation</h3>
                <p className="text-gray-500 text-sm font-medium">Nominate a manager to approve leave requests on your behalf</p>
              </div>
              {canEdit && (
                <Button
                  onClick={() => setDelegationModalOpen(true)}
                  className="rounded-xl bg-black text-white hover:bg-gray-800 h-10 px-4 font-semibold flex items-center gap-2"
                >
                  <UserPlus className="h-4 w-4" />
                  Nominate Manager
                </Button>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="text-xs text-gray-900 font-bold bg-white border-b border-gray-100">
                  <tr>
                    <th className="px-6 py-4">Status</th>
                    <th className="px-6 py-4">Original Manager</th>
                    <th className="px-6 py-4">Nominated Manager</th>
                    <th className="px-6 py-4 text-center">Period</th>
                    {canEdit && <th className="px-6 py-4 text-right">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {loading ? (
                    <tr>
                      <td colSpan={canEdit ? 5 : 4} className="px-6 py-12 text-center text-gray-500 italic">
                        <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                        Loading delegations...
                      </td>
                    </tr>
                  ) : delegations.length === 0 ? (
                    <tr>
                      <td colSpan={canEdit ? 5 : 4} className="px-6 py-12 text-center text-gray-500 italic">
                        No delegation authorities assigned.
                      </td>
                    </tr>
                  ) : (
                    delegations.map((row) => {
                      const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Australia/Sydney' });
                      const isActiveRange = today >= row.startDate && today <= row.endDate;
                      return (
                        <tr key={row.id} className="hover:bg-gray-50/50 transition-colors">
                          <td className="px-6 py-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-bold border ${isActiveRange
                              ? "bg-green-50 text-green-700 border-green-100"
                              : "bg-gray-50 text-gray-500 border-gray-100"
                              }`}>
                              {isActiveRange ? "Active" : "Scheduled/Past"}
                            </span>
                          </td>
                          <td className="px-6 py-4 font-medium text-gray-900">{row.originalManager.name}</td>
                          <td className="px-6 py-4 font-medium text-gray-900">{row.nominatedManager.name}</td>
                          <td className="px-6 py-4 text-center text-gray-600">
                            <div className="flex items-center justify-center gap-2">
                              <CalendarIcon className="h-3.5 w-3.5 text-gray-400" />
                              <span>{new Date(row.startDate).toLocaleDateString()} - {new Date(row.endDate).toLocaleDateString()}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-right">
                            {canEdit && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg"
                                onClick={() => handleDeleteDelegation(row.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="p-6 bg-blue-50/50 border-t border-blue-100">
              <div className="flex gap-3">
                <div className="shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                  <CalendarIcon className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-blue-900">How Delegation Works</h4>
                  <p className="text-xs text-blue-700 font-medium mt-0.5 leading-relaxed">
                    When you nominate a manager, they will receive authority to approve leave requests from your team within the specified date range.
                    Authority transfers automatically based on the current date.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "Accruals" && canManageTab("Accruals") && (
          <div className="bg-white rounded-[24px] border border-gray-100 shadow-sm overflow-hidden">
            <div className="p-6 border-b border-gray-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <h3 className="font-bold text-gray-900 text-lg">Leave Accruals Settings</h3>
                <p className="text-gray-500 text-sm font-medium">Hourly accrual, posted daily from each person's standard daily hours</p>
              </div>
              <div className="flex items-center gap-4 w-full md:w-auto">
                <div className="relative flex-1 md:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search staff..."
                    value={accrualSearchTerm}
                    onChange={(e) => {
                      setAccrualSearchTerm(e.target.value);
                      setCurrentPageAccruals(1);
                    }}
                    className="pl-9 rounded-xl border-gray-200 bg-gray-50/50 focus:bg-white h-10 text-sm"
                  />
                </div>
                {loading && <Loader2 className="h-5 w-5 animate-spin text-gray-400" />}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="text-xs text-gray-900 font-bold bg-white border-b border-gray-100">
                  <tr>
                    <th className="px-6 py-4 w-48">Staff Name</th>
                    <th className="px-4 py-4 w-44">Weekly Entitlement</th>
                    <th className="px-4 py-4 w-40">Accrual</th>
                    <th className="px-4 py-4 w-40">Start Accruing From</th>
                    {canEdit && <th className="px-6 py-4 w-24 text-right">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {loading ? (
                    <tr>
                      <td colSpan={canEdit ? 5 : 4} className="px-6 py-12 text-center text-gray-500 italic">
                        <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                        Loading accruals...
                      </td>
                    </tr>
                  ) : (() => {
                    const filtered = employeeAccruals
                      .filter(emp => emp.name.toLowerCase().includes(accrualSearchTerm.toLowerCase()))
                      .sort((a, b) => a.name.localeCompare(b.name));
                    if (filtered.length === 0) {
                      return (
                        <tr>
                          <td colSpan={canEdit ? 5 : 4} className="px-6 py-12 text-center text-gray-500 italic">
                            No team members found matching "{accrualSearchTerm}".
                          </td>
                        </tr>
                      );
                    }
                    const startIndex = (currentPageAccruals - 1) * ITEMS_PER_PAGE;
                    const paginated = filtered.slice(startIndex, startIndex + ITEMS_PER_PAGE);
                    return paginated.map((row) => (
                      <tr key={row.id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <Avatar className="h-8 w-8 text-xs font-medium border border-gray-100 shadow-sm">
                              <AvatarFallback className="bg-gray-50 text-gray-600">
                                {row.initials}
                              </AvatarFallback>
                            </Avatar>
                            <span className="text-gray-900 font-medium whitespace-nowrap">{row.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-gray-900 font-medium tabular-nums whitespace-nowrap">
                            {Number(row.annualRate).toFixed(4)}
                            <span className="text-gray-400 font-normal"> / {Number(row.personalRate).toFixed(4)} h</span>
                          </div>
                          <span className="text-[11px] text-gray-400">
                            Annual / Personal · {Number(row.standardWeeklyHours).toFixed(2)} h week
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={row.enabled}
                              onCheckedChange={(val) => updateAccrualLocal(row.id, 'enabled', val)}
                              disabled={!canEdit}
                            />
                            <span className="text-xs text-gray-500 font-medium">{row.enabled ? 'Daily' : 'Off'}</span>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <Input
                            type="date"
                            value={row.accrualStartDate || ""}
                            onChange={(e) => updateAccrualLocal(row.id, 'accrualStartDate', e.target.value || null)}
                            className="w-full h-9 rounded-lg border-gray-200 bg-white text-xs"
                            disabled={!canEdit}
                          />
                        </td>
                        <td className="px-6 py-4 text-right">
                          {canEdit && (
                            <Button
                              variant="outline"
                              title="Save Accrual Settings"
                              className="h-8 px-3 text-gray-700 font-bold hover:text-black hover:bg-gray-100 border-gray-200 rounded-lg flex items-center gap-2 ml-auto"
                              onClick={() => handleSaveAccrual(row)}
                              disabled={savingAccrualId === row.id}
                            >
                              {savingAccrualId === row.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                            </Button>
                          )}
                        </td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls for Accruals */}
            {(() => {
              const filtered = employeeAccruals
                .filter(emp => emp.name.toLowerCase().includes(accrualSearchTerm.toLowerCase()))
                .sort((a, b) => a.name.localeCompare(b.name));
              const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
              if (totalPages <= 1) return null;

              return (
                <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between bg-gray-50/30">
                  <p className="text-xs text-gray-500 font-medium whitespace-nowrap">
                    Showing <span className="font-bold text-gray-900">{Math.min(filtered.length, (currentPageAccruals - 1) * ITEMS_PER_PAGE + 1)}</span> to <span className="font-bold text-gray-900">{Math.min(filtered.length, currentPageAccruals * ITEMS_PER_PAGE)}</span> of <span className="font-bold text-gray-900">{filtered.length}</span> staff
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={currentPageAccruals === 1}
                      onClick={() => setCurrentPageAccruals(prev => Math.max(1, prev - 1))}
                      className="rounded-lg h-8 w-8 p-0 border-gray-200"
                    >
                      <ChevronLeft size={16} />
                    </Button>

                    <div className="flex items-center gap-1 mx-2">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                        if (page === 1 || page === totalPages || (page >= currentPageAccruals - 1 && page <= currentPageAccruals + 1)) {
                          return (
                            <button
                              key={page}
                              onClick={() => setCurrentPageAccruals(page)}
                              className={`h-8 w-8 rounded-lg text-xs font-bold transition-all ${currentPageAccruals === page
                                ? "bg-black text-white"
                                : "text-gray-500 hover:bg-gray-100"
                                }`}
                            >
                              {page}
                            </button>
                          );
                        }
                        if (page === 2 || page === totalPages - 1) {
                          return <span key={page} className="text-gray-400 text-xs px-1">...</span>;
                        }
                        return null;
                      })}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      disabled={currentPageAccruals === totalPages}
                      onClick={() => setCurrentPageAccruals(prev => Math.min(totalPages, prev + 1))}
                      className="rounded-lg h-8 w-8 p-0 border-gray-200"
                    >
                      <ChevronRight size={16} />
                    </Button>
                  </div>
                </div>
              );
            })()}
            <div className="p-6 bg-blue-50/50 border-t border-blue-100">
              <div className="flex gap-3">
                <div className="shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                  <CalendarIcon className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-blue-900">How Hourly Accruals Work</h4>
                  <p className="text-xs text-blue-700 font-medium mt-0.5 leading-relaxed">
                    Leave accrues per ordinary hour paid — 1/13 h Annual and 1/26 h Personal — from each person's
                    standard daily hours (Daily Hours tab), and is posted at the end of every working day. A day that
                    includes Leave Without Pay earns proportionally less: the unpaid hours earn nothing. Casuals accrue
                    no leave.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "Daily Hours" && canManageTab("Daily Hours") && (
          <div className="bg-white rounded-[24px] border border-gray-100 shadow-sm overflow-hidden">
            <div className="p-6 border-b border-gray-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <h3 className="font-bold text-gray-900 text-lg">Standard Daily Hours</h3>
                <p className="text-gray-500 text-sm font-medium">Configure full-day hours per employee for accurate leave deductions</p>
              </div>
              <div className="flex items-center gap-4 w-full md:w-auto">
                <div className="relative flex-1 md:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search staff..."
                    value={dailyHoursSearchTerm}
                    onChange={(e) => {
                      setDailyHoursSearchTerm(e.target.value);
                      setCurrentPageDailyHours(1);
                    }}
                    className="pl-9 rounded-xl border-gray-200 bg-gray-50/50 focus:bg-white h-10 text-sm"
                  />
                </div>
                {loading && <Loader2 className="h-5 w-5 animate-spin text-gray-400" />}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="text-xs text-gray-900 font-bold bg-white border-b border-gray-100">
                  <tr>
                    <th className="px-6 py-4 w-48">Staff Name</th>
                    <th className="px-6 py-4 w-40">Standard Daily Hours</th>
                    <th className="px-6 py-4 w-32">Half Day Hours</th>
                    {canEdit && <th className="px-6 py-4 w-24 text-right">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {loading ? (
                    <tr>
                      <td colSpan={canEdit ? 4 : 3} className="px-6 py-12 text-center text-gray-500 italic">
                        <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                        Loading daily hours...
                      </td>
                    </tr>
                  ) : (() => {
                    const filtered = employeeDailyHours
                      .filter(emp => emp.name.toLowerCase().includes(dailyHoursSearchTerm.toLowerCase()))
                      .sort((a, b) => a.name.localeCompare(b.name));
                    if (filtered.length === 0) {
                      return (
                        <tr>
                          <td colSpan={canEdit ? 4 : 3} className="px-6 py-12 text-center text-gray-500 italic">
                            No team members found matching "{dailyHoursSearchTerm}".
                          </td>
                        </tr>
                      );
                    }
                    const startIndex = (currentPageDailyHours - 1) * ITEMS_PER_PAGE;
                    const paginated = filtered.slice(startIndex, startIndex + ITEMS_PER_PAGE);
                    return paginated.map((row) => (
                      <tr key={row.id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <Avatar className="h-8 w-8 text-xs font-medium border border-gray-100 shadow-sm">
                              <AvatarFallback className="bg-gray-50 text-gray-600">
                                {row.initials}
                              </AvatarFallback>
                            </Avatar>
                            <span className="text-gray-900 font-medium whitespace-nowrap">{row.name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <Input
                            type="number"
                            min="0.5"
                            step="0.5"
                            max="24"
                            value={row.standardDailyHours}
                            onChange={(e) => updateDailyHoursLocal(row.id, parseFloat(e.target.value) || 0)}
                            className="w-28 h-9 rounded-lg border-gray-200 bg-white"
                            disabled={!canEdit}
                          />
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-gray-600 font-medium">
                            {(row.standardDailyHours / 2).toFixed(2)} hrs
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          {canEdit && (
                            <Button
                              variant="outline"
                              title="Save Daily Hours"
                              className="h-8 px-3 text-gray-700 font-bold hover:text-black hover:bg-gray-100 border-gray-200 rounded-lg flex items-center gap-2 ml-auto"
                              onClick={() => handleSaveDailyHours(row)}
                              disabled={savingDailyHoursId === row.id}
                            >
                              {savingDailyHoursId === row.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                            </Button>
                          )}
                        </td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls for Daily Hours */}
            {(() => {
              const filtered = employeeDailyHours
                .filter(emp => emp.name.toLowerCase().includes(dailyHoursSearchTerm.toLowerCase()))
                .sort((a, b) => a.name.localeCompare(b.name));
              const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
              if (totalPages <= 1) return null;

              return (
                <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between bg-gray-50/30">
                  <p className="text-xs text-gray-500 font-medium whitespace-nowrap">
                    Showing <span className="font-bold text-gray-900">{Math.min(filtered.length, (currentPageDailyHours - 1) * ITEMS_PER_PAGE + 1)}</span> to <span className="font-bold text-gray-900">{Math.min(filtered.length, currentPageDailyHours * ITEMS_PER_PAGE)}</span> of <span className="font-bold text-gray-900">{filtered.length}</span> staff
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={currentPageDailyHours === 1}
                      onClick={() => setCurrentPageDailyHours(prev => Math.max(1, prev - 1))}
                      className="rounded-lg h-8 w-8 p-0 border-gray-200"
                    >
                      <ChevronLeft size={16} />
                    </Button>

                    <div className="flex items-center gap-1 mx-2">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                        if (page === 1 || page === totalPages || (page >= currentPageDailyHours - 1 && page <= currentPageDailyHours + 1)) {
                          return (
                            <button
                              key={page}
                              onClick={() => setCurrentPageDailyHours(page)}
                              className={`h-8 w-8 rounded-lg text-xs font-bold transition-all ${currentPageDailyHours === page
                                ? "bg-black text-white"
                                : "text-gray-500 hover:bg-gray-100"
                                }`}
                            >
                              {page}
                            </button>
                          );
                        }
                        if (page === 2 || page === totalPages - 1) {
                          return <span key={page} className="text-gray-400 text-xs px-1">...</span>;
                        }
                        return null;
                      })}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      disabled={currentPageDailyHours === totalPages}
                      onClick={() => setCurrentPageDailyHours(prev => Math.min(totalPages, prev + 1))}
                      className="rounded-lg h-8 w-8 p-0 border-gray-200"
                    >
                      <ChevronRight size={16} />
                    </Button>
                  </div>
                </div>
              );
            })()}
            <div className="p-6 bg-blue-50/50 border-t border-blue-100">
              <div className="flex gap-3">
                <div className="shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                  <CalendarIcon className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-blue-900">How Standard Daily Hours Work</h4>
                  <p className="text-xs text-blue-700 font-medium mt-0.5 leading-relaxed">
                    Set the standard daily hours for each employee. When a manager reports a Full Day absence, it will deduct these hours. A Half Day will deduct half of this value. For example, a part-timer with 6 daily hours will have 6 hours deducted for a full day and 3 hours for a half day.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "Balance Movements" && canManageTab("Balance Movements") && (
          <div className="bg-white rounded-[24px] border border-gray-100 shadow-sm overflow-hidden">
            <div className="p-6 border-b border-gray-100 flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
              <div>
                <h3 className="font-bold text-gray-900 text-lg">Balance Movements</h3>
                <p className="text-gray-500 text-sm font-medium">Comprehensive audit of all leave balance changes</p>
              </div>
              <div className="flex items-center gap-3 w-full lg:w-auto flex-wrap">
                <div className="relative flex-1 lg:w-56">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search employee..."
                    value={movementSearchTerm}
                    onChange={(e) => setMovementSearchTerm(e.target.value)}
                    className="pl-9 rounded-xl border-gray-200 bg-gray-50/50 focus:bg-white h-10 text-sm"
                  />
                </div>
                <Select value={movementLeaveTypeFilter} onValueChange={setMovementLeaveTypeFilter}>
                  <SelectTrigger className="w-40 rounded-xl h-10 text-sm border-gray-200 bg-gray-50/50">
                    <SelectValue placeholder="Leave Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="All">All Types</SelectItem>
                    <SelectItem value="Annual Leave">Annual Leave</SelectItem>
                    <SelectItem value="Personal Leave">Personal Leave</SelectItem>
                    <SelectItem value="Unpaid Leave">Unpaid Leave</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={movementTypeFilter} onValueChange={setMovementTypeFilter}>
                  <SelectTrigger className="w-44 rounded-xl h-10 text-sm border-gray-200 bg-gray-50/50">
                    <SelectValue placeholder="Movement" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="All">All Movements</SelectItem>
                    <SelectItem value="Accrual">Accrual</SelectItem>
                    <SelectItem value="Accrual Recalculation">Accrual Recalculation</SelectItem>
                    <SelectItem value="Manual Adjustment">Manual Adjustment</SelectItem>
                    <SelectItem value="Leave Approved">Leave Approved</SelectItem>
                    <SelectItem value="Leave Rejected">Leave Rejected</SelectItem>
                    <SelectItem value="Leave Revoked">Leave Revoked</SelectItem>
                    <SelectItem value="Leave Cancelled">Leave Cancelled</SelectItem>
                    <SelectItem value="Absence Reported">Absence Reported</SelectItem>
                    <SelectItem value="Absence Deleted">Absence Deleted</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  onClick={handleExportMovements}
                  disabled={movements.length === 0}
                  className="rounded-xl h-10 text-sm font-semibold border-gray-200"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
              </div>
            </div>

            <div className="p-4 border-b border-gray-100 flex flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-gray-500">From</span>
                <Input
                  type="date"
                  value={movementStartDate}
                  onChange={(e) => setMovementStartDate(e.target.value)}
                  className="rounded-xl h-9 text-sm border-gray-200 w-40"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-gray-500">To</span>
                <Input
                  type="date"
                  value={movementEndDate}
                  onChange={(e) => setMovementEndDate(e.target.value)}
                  className="rounded-xl h-9 text-sm border-gray-200 w-40"
                />
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setMovementStartDate("");
                  setMovementEndDate("");
                  setMovementLeaveTypeFilter("All");
                  setMovementTypeFilter("All");
                  setMovementSearchTerm("");
                }}
                className="text-xs font-semibold text-gray-500 hover:text-gray-700"
              >
                Clear Filters
              </Button>
            </div>

            {loading && movements.length === 0 ? (
              <div className="p-12 flex justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50/50 text-left">
                      <th
                        className="px-6 py-3 font-bold text-gray-700 text-xs uppercase tracking-wider cursor-pointer select-none"
                        onClick={() => setMovementSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')}
                      >
                        <div className="flex items-center gap-1">
                          Date Approved
                          {movementSortDirection === 'asc' ? (
                            <ArrowUp className="h-3 w-3 text-gray-500" />
                          ) : (
                            <ArrowDown className="h-3 w-3 text-gray-500" />
                          )}
                        </div>
                      </th>
                      <th className="px-6 py-3 font-bold text-gray-700 text-xs uppercase tracking-wider">Effective Date</th>
                      <th className="px-6 py-3 font-bold text-gray-700 text-xs uppercase tracking-wider">Employee</th>
                      <th className="px-6 py-3 font-bold text-gray-700 text-xs uppercase tracking-wider">Movement</th>
                      <th className="px-6 py-3 font-bold text-gray-700 text-xs uppercase tracking-wider">Leave Type</th>
                      <th className="px-6 py-3 font-bold text-gray-700 text-xs uppercase tracking-wider text-right">Hours</th>
                      <th className="px-6 py-3 font-bold text-gray-700 text-xs uppercase tracking-wider">Description</th>
                      <th className="px-6 py-3 font-bold text-gray-700 text-xs uppercase tracking-wider">Authorised By</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(() => {
                      const filtered = movements.filter(m => {
                        const matchesSearch = !movementSearchTerm ||
                          (m.employee?.name || "").toLowerCase().includes(movementSearchTerm.toLowerCase());
                        const matchesLeaveType = movementLeaveTypeFilter === "All" || m.leaveType === movementLeaveTypeFilter;
                        const matchesMovementType = movementTypeFilter === "All" || m.movementType === movementTypeFilter;
                        const moveDate = new Date(m.effectiveDate || m.date);
                        const matchesStart = !movementStartDate || moveDate >= new Date(movementStartDate);
                        const matchesEnd = !movementEndDate || moveDate <= new Date(movementEndDate + 'T23:59:59');
                        return matchesSearch && matchesLeaveType && matchesMovementType && matchesStart && matchesEnd;
                      }).sort((a, b) => {
                        const dateA = new Date(a.date).getTime();
                        const dateB = new Date(b.date).getTime();
                        return movementSortDirection === 'asc' ? dateA - dateB : dateB - dateA;
                      });

                      const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
                      const paginated = filtered.slice(
                        (currentPageMovements - 1) * ITEMS_PER_PAGE,
                        currentPageMovements * ITEMS_PER_PAGE
                      );

                      if (filtered.length === 0) {
                        return (
                          <tr>
                            <td colSpan={8} className="px-6 py-12 text-center text-gray-500 italic">
                              {movementSearchTerm || movementLeaveTypeFilter !== "All" || movementTypeFilter !== "All"
                                ? "No movements match the current filters."
                                : "No balance movements found."}
                            </td>
                          </tr>
                        );
                      }

                      return (
                        <>
                          {paginated.map((m, idx) => {
                            const badgeColors: Record<string, string> = {
                              "Accrual": "bg-blue-100 text-blue-700",
                              "Accrual Recalculation": "bg-amber-100 text-amber-700",
                              "Manual Adjustment": "bg-purple-100 text-purple-700",
                              "Leave Approved": "bg-red-100 text-red-700",
                              "Leave Rejected": "bg-green-100 text-green-700",
                              "Leave Revoked": "bg-emerald-100 text-emerald-700",
                              "Leave Cancelled": "bg-rose-100 text-rose-700",
                              "Absence Reported": "bg-orange-100 text-orange-700",
                              "Absence Deleted": "bg-green-100 text-green-700",
                            };

                            return (
                              <tr key={`${m.id}-${m.movementType}-${idx}`} className="hover:bg-gray-50/50 transition-colors">
                                <td className="px-6 py-3 text-gray-900 font-medium whitespace-nowrap">
                                  {new Date(m.approvedDate || m.date).toLocaleDateString()}
                                </td>
                                <td className="px-6 py-3 text-gray-700 whitespace-nowrap">
                                  {m.metadata?.startDate && m.metadata?.endDate && m.metadata.startDate !== m.metadata.endDate
                                    ? `${new Date(m.metadata.startDate).toLocaleDateString()} – ${new Date(m.metadata.endDate).toLocaleDateString()}`
                                    : m.effectiveDate ? new Date(m.effectiveDate).toLocaleDateString() : '—'}
                                </td>
                                <td className="px-6 py-3 text-gray-900 font-medium">
                                  {m.employee?.name || 'N/A'}
                                </td>
                                <td className="px-6 py-3">
                                  <div className="flex items-center gap-2">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${badgeColors[m.movementType] || 'bg-gray-100 text-gray-700'}`}>
                                      {m.movementType}
                                    </span>
                                    {m.metadata?.handover && (
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        title="View handover confirmation"
                                        aria-label="View handover confirmation"
                                        onClick={() => setHandoverMovement(m)}
                                        className="h-6 w-6 rounded-md text-blue-600 hover:bg-blue-50 hover:text-blue-700 shrink-0"
                                      >
                                        <ClipboardCheck className="h-3.5 w-3.5" />
                                      </Button>
                                    )}
                                  </div>
                                </td>
                                <td className="px-6 py-3 text-gray-700">
                                  {m.leaveType}
                                </td>
                                <td className={`px-6 py-3 text-right font-bold ${Number(m.hours) > 0 ? 'text-green-600' : Number(m.hours) < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                                  {Number(m.hours) > 0 ? `+${m.hours}` : m.hours}
                                </td>
                                <td className="px-6 py-3 text-gray-700 max-w-xs truncate" title={m.description}>
                                  {m.description}
                                </td>
                                <td className="px-6 py-3 text-gray-700">
                                  {m.performedBy?.name || 'System'}
                                </td>
                              </tr>
                            );
                          })}
                        </>
                      );
                    })()}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {(() => {
              const filtered = movements.filter(m => {
                const matchesSearch = !movementSearchTerm ||
                  (m.employee?.name || "").toLowerCase().includes(movementSearchTerm.toLowerCase());
                const matchesLeaveType = movementLeaveTypeFilter === "All" || m.leaveType === movementLeaveTypeFilter;
                const matchesMovementType = movementTypeFilter === "All" || m.movementType === movementTypeFilter;
                const moveDate = new Date(m.effectiveDate || m.date);
                const matchesStart = !movementStartDate || moveDate >= new Date(movementStartDate);
                const matchesEnd = !movementEndDate || moveDate <= new Date(movementEndDate + 'T23:59:59');
                return matchesSearch && matchesLeaveType && matchesMovementType && matchesStart && matchesEnd;
              }).sort((a, b) => {
                const dateA = new Date(a.date).getTime();
                const dateB = new Date(b.date).getTime();
                return movementSortDirection === 'asc' ? dateA - dateB : dateB - dateA;
              });
              const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
              if (totalPages <= 1) return null;

              return (
                <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between bg-gray-50/30">
                  <p className="text-xs text-gray-500 font-medium whitespace-nowrap">
                    Showing <span className="font-bold text-gray-900">{Math.min(filtered.length, (currentPageMovements - 1) * ITEMS_PER_PAGE + 1)}</span> to <span className="font-bold text-gray-900">{Math.min(filtered.length, currentPageMovements * ITEMS_PER_PAGE)}</span> of <span className="font-bold text-gray-900">{filtered.length}</span> movements
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={currentPageMovements === 1}
                      onClick={() => setCurrentPageMovements(prev => Math.max(1, prev - 1))}
                      className="rounded-lg h-8 w-8 p-0 border-gray-200"
                    >
                      <ChevronLeft size={16} />
                    </Button>
                    <span className="text-xs font-bold text-gray-700">
                      Page {currentPageMovements} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={currentPageMovements === totalPages}
                      onClick={() => setCurrentPageMovements(prev => Math.min(totalPages, prev + 1))}
                      className="rounded-lg h-8 w-8 p-0 border-gray-200"
                    >
                      <ChevronRight size={16} />
                    </Button>
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* Handover Confirmation Dialog — opened from the Balance Movements feed */}
      <Dialog open={!!handoverMovement} onOpenChange={(open) => !open && setHandoverMovement(null)}>
        <DialogContent className="rounded-[24px] border-gray-200 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="mx-auto sm:mx-0 h-12 w-12 rounded-2xl bg-blue-50 border border-blue-100 flex items-center justify-center mb-2">
              <ClipboardCheck className="text-blue-600" size={24} />
            </div>
            <DialogTitle className="text-xl font-bold text-gray-900">Leave handover record</DialogTitle>
            <DialogDescription className="text-gray-500 text-sm">
              {handoverMovement?.employee?.name || 'Employee'}
              {handoverMovement?.leaveType ? ` • ${handoverMovement.leaveType}` : ''}
              {handoverMovement?.metadata?.startDate
                ? ` • ${new Date(handoverMovement.metadata.startDate).toLocaleDateString()} – ${new Date(handoverMovement.metadata.endDate || handoverMovement.metadata.startDate).toLocaleDateString()}`
                : ''}
            </DialogDescription>
          </DialogHeader>

          <LeaveHandoverSummary handover={handoverMovement?.metadata?.handover} variant="plain" />

          <p className="text-[10px] text-gray-400 font-medium leading-relaxed">
            Recorded when the leave request was submitted and last amended. Responses cannot be changed by the employee after submission.
          </p>

          <DialogFooter>
            <Button
              type="button"
              onClick={() => setHandoverMovement(null)}
              className="rounded-xl font-medium px-5 bg-black text-white hover:bg-gray-800 w-full sm:w-auto"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Nominate Manager Modal */}
      {delegationModalOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-[20px] p-6 w-full max-w-md shadow-xl animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Nominate Delegate</h2>
                <p className="text-sm text-gray-500 mt-1">Assign leave approval authority to another manager</p>
              </div>
              <button
                onClick={() => setDelegationModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors bg-gray-50 hover:bg-gray-100 rounded-full p-1 border border-gray-100"
                disabled={submitting}
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-5">
              <div className="space-y-2">
                <label className="text-sm font-bold text-gray-900">Nominated Manager</label>
                <Select
                  value={delegationData.nominatedManagerId}
                  onValueChange={(val) => setDelegationData(prev => ({ ...prev, nominatedManagerId: val }))}
                  disabled={submitting}
                >
                  <SelectTrigger className="rounded-xl border-gray-200 bg-gray-50/50 h-11 focus:bg-white text-gray-900">
                    <SelectValue placeholder="Select a manager" />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl border-gray-200 bg-white">
                    {eligibleDelegates.map(user => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.name} ({user.userRole})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-900">Start Date</label>
                  <Input
                    type="date"
                    value={delegationData.startDate}
                    onChange={(e) => setDelegationData(prev => ({ ...prev, startDate: e.target.value }))}
                    className="rounded-xl border-gray-200 bg-white h-11 focus:border-gray-300 text-gray-900"
                    disabled={submitting}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-900">End Date</label>
                  <Input
                    type="date"
                    value={delegationData.endDate}
                    onChange={(e) => setDelegationData(prev => ({ ...prev, endDate: e.target.value }))}
                    className="rounded-xl border-gray-200 bg-white h-11 focus:border-gray-300 text-gray-900"
                    disabled={submitting}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-8">
              <Button
                variant="outline"
                className="rounded-xl border-gray-200 bg-white hover:bg-gray-50 h-11 px-6 font-semibold"
                onClick={() => setDelegationModalOpen(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                className="rounded-xl bg-black text-white hover:bg-gray-800 h-11 px-6 font-semibold flex items-center gap-2"
                onClick={handleSaveDelegation}
                disabled={submitting}
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Assign Authority
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveAdmin;